import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { AuthLeftPanelComponent } from '../../shared/components/auth-left-panel/auth-left-panel.component';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SendResetPasswordEmailRequest } from '../../shared/models/auth.model';
import { UnauthorizedLayoutComponent } from '../../shared/components/unauthorized-layout/unauthorized-layout.component';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  templateUrl: './forgot-password.html',
  styleUrls: ['./forgot-password.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AuthLeftPanelComponent,
    UnauthorizedLayoutComponent
]
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    // Subscribe to auth state for loading
    this.authService.authState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isLoading = state.loading;
      });
  }

  /**
   * Submit forgot password form
   */
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.errorMessage = null;
    this.successMessage = null;
    this.isLoading = true;

    const request: SendResetPasswordEmailRequest = {
      email: this.form.value.email
    };

    this.authService.sendResetPasswordEmail(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.successMessage = response.data.message || response.message;
            this.form.reset();
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 5000);
          } else {
            this.errorMessage = response.message || 'Failed to send reset password email';
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          let errorMsg = error?.message || 'Failed to send reset password email. Please try again.';
          this.errorMessage = errorMsg;
        }
      });
  }

  /**
   * Get error message for field
   */
  getFieldError(fieldName: string): string | null {
    const control = this.form.get(fieldName);
    if (!control || !control.touched) return null;

    if (control.hasError('required')) {
      return 'Email is required';
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    return null;
  }

  /**
   * Navigate back to login
   */
  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
